import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getMatterJurisdictionAnalyticsProps } from '@/analytics/utils/matter-jurisdiction-analytics'
import { useAppDispatch } from '@/store/store-hooks'
import { store } from '@/store/store'
import patchUpdateAgentConversationMatter from '@/agent/conversations/fetch/patch-update-agent-conversation-matter'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { postCreateMatter } from '@/matter/fetch/post-create-matter'
import { CreateOrUpdateMatterForm, MatterRead } from '@/matter/store/schemas'
import { MattersActions } from '@/matter/store/slice'
import { MatterCreationError, ConversationMatterAssignmentError } from '@/matter/errors'

type UseAssignConversationMatterOptions = {
  conversationId: string
}

/**
 * useAssignConversationMatter - Assign a matter to a conversation
 * @param conversationId - The ID of the conversation to assign the matter to
 * @returns A function to assign a matter to the conversation
 */
export default function useAssignConversationMatter({ conversationId }: UseAssignConversationMatterOptions) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  /**
   * assignMatter - Assign a matter to a conversation
   * @param matterId - The ID of the matter to assign to the conversation
   * @param onSuccess - A callback function to call when the matter is assigned successfully
   * @returns A promise that resolves when the matter is assigned
   */
  const assignMatter = async (matterId: string | null, onSuccess?: () => void): Promise<void> => {
    try {
      const previousMatterId = selectAgentConversationMatterId(store.getState(), { conversationId })
      await patchUpdateAgentConversationMatter({ conversationId, matterId })
      dispatch(AgentConversationsActions.updateConversationMatter({ conversationId, matterId }))
      // The conversation moved between matters; reconcile both the source and destination lists.
      dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId: previousMatterId, forceRefresh: true }))
      dispatch(AgentConversationsActions.refreshMatterConversationList({ matterId, forceRefresh: true }))
      onSuccess?.()
    } catch (err) {
      console.error('Error assigning matter:', err)
      throw err
    }
  }

  /**
   * assignToNewMatter - Create a new matter and assign the conversation to it
   * @param matterData - The data for creating the new matter
   * @param onSuccess - A callback function to call when the matter is created and assigned successfully
   * @returns A promise that resolves with the created matter
   */
  const assignToNewMatter = async (matterData: CreateOrUpdateMatterForm, onSuccess?: (matter: MatterRead) => void): Promise<MatterRead> => {
    let newMatter: MatterRead

    // Create the new matter
    try {
      newMatter = await postCreateMatter(matterData)

      trackEvent(AnalyticsEvent.MatterCreated, getMatterJurisdictionAnalyticsProps(newMatter.default_jurisdiction))

      // Update matter state
      dispatch(MattersActions.upsertMatter(newMatter))
    } catch (err) {
      throw new MatterCreationError(err)
    }

    // Assign conversation to the new matter
    try {
      await assignMatter(newMatter.id)
    } catch (err) {
      throw new ConversationMatterAssignmentError(err)
    }

    onSuccess?.(newMatter)
    return newMatter
  }

  return { assignMatter, assignToNewMatter }
}
